import React from 'react';
import './Chip.css';

export interface ChipProps {
  /** Chip content/label */
  children: React.ReactNode;
  /** Whether the chip is selected */
  selected?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Color variant */
  variant?:
    | 'default'
    | 'primary'
    | 'minimal'
    | 'success'
    | 'warning'
    | 'danger'
    | 'curated'
    | 'personal'
    | 'ai'
    | 'rating-hard'
    | 'rating-medium'
    | 'rating-easy';
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Whether the chip is disabled */
  disabled?: boolean;
  /** Whether to use pill/round shape */
  round?: boolean;
  /** Additional content (e.g., badge/count) */
  badge?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Chip/Tag component for labels and filters.
 * Inspired by Blueprint Tag component with compact, professional styling.
 */
export const Chip: React.FC<ChipProps> = ({
  children,
  selected = false,
  onClick,
  variant = 'default',
  size = 'medium',
  disabled = false,
  round = false,
  badge,
  className = '',
}) => {
  const isInteractive = !!onClick && !disabled;

  const Component = isInteractive ? 'button' : 'span';

  const classes = [
    'stc-chip',
    variant !== 'default' && `stc-chip--${variant}`,
    size !== 'medium' && `stc-chip--${size}`,
    selected && 'stc-chip--selected',
    disabled && 'stc-chip--disabled',
    round && 'stc-chip--round',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component
      className={classes}
      onClick={isInteractive ? onClick : undefined}
      disabled={disabled}
      type={isInteractive ? 'button' : undefined}
      role={isInteractive ? 'button' : undefined}
      aria-pressed={isInteractive ? selected : undefined}
    >
      <span className="stc-chip__label">{children}</span>
      {badge !== undefined && <span className="stc-chip__badge">{badge}</span>}
    </Component>
  );
};

export interface ChipGroupProps {
  /** Chip elements */
  children: React.ReactNode;
  /** Label for the group */
  label?: string;
  /** Additional CSS class */
  className?: string;
}

/**
 * Chip group container for organizing related chips.
 */
export const ChipGroup: React.FC<ChipGroupProps> = ({ children, label, className = '' }) => {
  return (
    <div className={`stc-chip-group ${className}`}>
      {label && <span className="stc-chip-group__label">{label}</span>}
      <div className="stc-chip-group__chips">{children}</div>
    </div>
  );
};
